import { ListTodo } from "lucide-react";
import * as React from "react";
import { Page, PageHeader } from "@/components/layout/page";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { TaskRows } from "@/features/task-list";
import { useAuth } from "@/lib/auth";
import { notThere, overdue, useTasks, type TaskStatus, type TaskWhose } from "@/lib/tasks";
import { atLeast } from "@/lib/types";
import { useNow } from "@/lib/use-now";
import { cn } from "@/lib/utils";

/**
 * Tasks (ACL-408): the signed-in person's to dos about leads, made by workflows' Create task steps,
 * open ones first, the most overdue at the top. A supervisor or an admin can also read everybody's.
 * Every signed-in person, as Contacts is: a rep given a task must be able to find it.
 */
export function TasksPage() {
  const { user } = useAuth();
  const canSeeEveryone = atLeast(user?.role, "supervisor");
  const [whose, setWhose] = React.useState<TaskWhose>("mine");
  const [status, setStatus] = React.useState<TaskStatus>("open");
  const tasks = useTasks(canSeeEveryone ? whose : "mine", status);
  const now = useNow(30_000);
  const late = (tasks.data ?? []).filter((t) => overdue(t, now)).length;
  return (
    <Page className="max-w-4xl">
      <PageHeader
        title="Tasks"
        description={
          whose === "mine" ? "Your to dos about leads, made by workflows." : "Everybody's to dos about leads, made by workflows."
        }
      />
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
          <Segments
            label="Which tasks"
            value={status}
            onChange={setStatus}
            options={[
              { value: "open", label: "Open" },
              { value: "done", label: "Done" },
              { value: "all", label: "All" },
            ]}
          />
          {canSeeEveryone && (
            <Segments
              label="Whose tasks"
              value={whose}
              onChange={setWhose}
              options={[
                { value: "mine", label: "Mine" },
                { value: "everyone", label: "Everyone's" },
              ]}
            />
          )}
        </div>
        <div className="px-4">
          {tasks.isPending ? (
            <div className="flex justify-center py-10">
              <Spinner className="size-4" />
            </div>
          ) : notThere(tasks.error) ? (
            <EmptyState
              icon={ListTodo}
              title="Tasks are not on this server yet"
              description="They come with the core-api that runs workflows' Create task step."
            />
          ) : tasks.error ? (
            <ErrorState error={tasks.error} title="Could not load the tasks" />
          ) : tasks.data.length === 0 ? (
            <EmptyState
              icon={ListTodo}
              title={status === "done" ? "Nothing done yet" : "Nothing to do"}
              description="A workflow's Create task step puts tasks here, for the person it names."
            />
          ) : (
            <>
              {status !== "done" && late > 0 && (
                <p className="pt-3 text-xs font-medium text-danger-11">
                  {late === 1 ? "1 task is overdue." : `${late} tasks are overdue.`}
                </p>
              )}
              <TaskRows tasks={tasks.data} showAssignee={whose === "everyone"} />
            </>
          )}
        </div>
      </Card>
    </Page>
  );
}

function Segments<T extends string>({
  label, value, onChange, options,
}: {
  label: string;
  value: T;
  onChange: (next: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="inline-flex rounded-md border border-border p-0.5" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded px-2.5 py-1 text-xs font-medium transition-colors",
            value === o.value ? "bg-accent-2 text-accent-11" : "text-text-muted hover:text-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
